import {
  faExclamationCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import Box from '~/common/components/Box';
import Button from '~/common/components/Buttons/Button';
import Center from '~/common/components/Center';
import Flex from '~/common/components/Flex';
import AlertOkModal from '~/common/components/Modals/AlertOkModal';
import Modal from '~/common/components/Modals/Modal';
import ModalBody from '~/common/components/Modals/ModalBody';
import ModalCloseButton from '~/common/components/Modals/ModalCloseButton';
import ModalContent from '~/common/components/Modals/ModalContent';
import ModalFooter from '~/common/components/Modals/ModalFooter';
import ModalOverlay from '~/common/components/Modals/ModalOverlay';
import Text from '~/common/components/Texts/Text';
import { ALERT_SEVERITY } from '~/common/constants';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { navigateTo } from '~/common/util/browserNavigation';
import lang from '~/pages/Home/language.json';

import { useModalState } from './modalStore';

type ModalIconProps = Readonly<{
  severity?: number;
}>;

function ModalIcon({ severity }: ModalIconProps) {
  const commonStyle = { marginBottom: '8px', fontSize: '32px' };

  switch (severity) {
    case ALERT_SEVERITY.WARNING:
      return (
        <Box
          as="span"
          sx={commonStyle}
          color="orange.300"
          id="bailout-warning-icon"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </Box>
      );

    case ALERT_SEVERITY.ALERT:
      return (
        <Box as="span" sx={commonStyle} color="red.500" id="bailout-error-icon">
          <FontAwesomeIcon icon={faExclamationCircle} />
        </Box>
      );
    default:
      return null;
  }
}

type BailOutModalLayoutProps = {
  isOpen: boolean;
  onClose: (...args: any[]) => any;
  children?: any;
  footer: (...args: any[]) => any;
};

function BailOutModalLayout({
  isOpen,
  onClose,
  children,
  footer,
}: Readonly<BailOutModalLayoutProps>) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginTop={350}>
        <ModalCloseButton />
        <ModalBody mt="24px">{children}</ModalBody>
        <ModalFooter display="flex" justifyContent="center" pb="2rem">
          <Flex justifyContent="space-between" gridGap="12px">
            {footer()}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function BailOutModal() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isOpen, onClose } = useModalState('bailout');

  const {
    mutateAsync,
    data,
    isLoading: isBailOutUpdating,
    isError,
    error,
  } = useMutation({
    async mutationFn(confirmed: boolean) {
      const { data } =
        await typedApiClient.v1.submissionBailOutAndSubmitLoanToSetup({
          obfuscatedLoanIdentifier,
          confirmed,
        });
      return data;
    },
    onSuccess(alert) {
      if (alert) {
        return;
      }

      navigateTo(
        `${globalThis.globalConfig.ease.baseUrl}/Loan/${obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId}/${obfuscatedLoanIdentifier.obfuscatedLoanRecordId}/LoanApplication`,
      );
    },
  });

  const modalData: typeof data | null =
    (error as AxiosError<typeof data>)?.response?.data || data;

  // handlers
  const handleYes = async () => {
    try {
      const response = await mutateAsync(false);
      // if there is an error or an alert to confirm, don't mark bailed out
      if (!response) {
        onClose();
      }
    } catch {
      // Don't close the modal if there is an error returned to the user
    }
  };

  const handleAlertOk = async () => {
    if (!isError) {
      await mutateAsync(true);
    }

    onClose();
  };

  if (modalData) {
    return (
      <AlertOkModal
        isOpen={isOpen}
        handleClose={onClose}
        handleOk={handleAlertOk}
        loading={isBailOutUpdating}
        data={modalData}
      />
    );
  }

  return (
    <BailOutModalLayout
      isOpen={isOpen}
      onClose={onClose}
      footer={() => (
        <>
          <Button
            backgroundColor="blue.600"
            color="white"
            isLoading={isBailOutUpdating}
            loadingText={lang.YES_BUTTON}
            _hover={{
              backgroundColor: 'blue.500',
            }}
            onClick={handleYes}
          >
            {lang.YES_BUTTON}
          </Button>
          <Button
            colorScheme="blue"
            variant="outline"
            disabled={isBailOutUpdating}
            onClick={onClose}
          >
            {lang.NO_BUTTON}
          </Button>
        </>
      )}
    >
      <Center>
        <ModalIcon severity={ALERT_SEVERITY.WARNING} />
      </Center>
      <Text as="h3" textAlign="center" mb="16px">
        {lang.BAIL_OUT_TITLE}
      </Text>
      <Text as="p" fontSize="14px" textAlign="center" lineHeight="1.5rem">
        {lang.BAIL_OUT_QUESTION}
      </Text>
    </BailOutModalLayout>
  );
}
